/**
 * This file defines the recommendations3P() service.
 */
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type {
	AbstractServiceMarker,
	AnyServiceWithDeps,
	DependenciesContext,
} from '../../core/types';
import {
	type AnalyticsPayload,
	createAnalyticsEvent,
	fireErrorAnalytics,
	getCurrentCloudId,
	getCurrentProduct,
	type Product,
} from '../common/common';
import { type FetchInput, fetchJSON } from '../common/fetchJSON';

export interface UserInfo {
	atlassianAccountId?: string;
	avatarUrl: string;
	collaborationStrength: number;
	displayName: string;
	email: string;
	source: string;
	thirdPartyUserId: string;
}

type Recommendation3PResult = {
	recommendations: UserInfo[];
};

export type Recommendations3PInput = {
	limit: number;
	targetCloudId?: string;
	targetProduct?: Product;
};

const dependencies: {
	createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
	fetchJSON: (<Response>(input: FetchInput) => Promise<Response>) & AbstractServiceMarker;
	fireErrorAnalytics: ((input: AnalyticsPayload) => unknown) & AbstractServiceMarker;
	getCurrentCloudId: (() => string) & AbstractServiceMarker;
	getCurrentProduct: (() => Product) & AbstractServiceMarker;
} = {
	fetchJSON,
	createAnalyticsEvent,
	fireErrorAnalytics,
	getCurrentCloudId,
	getCurrentProduct,
};
type Deps = typeof dependencies;

export const fetch3PInviteRecommendations: typeof fetch3PInviteRecommendationsImpl & {
	dependencies: {
		createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
		fetchJSON: (<Response>(input: FetchInput) => Promise<Response>) & AbstractServiceMarker;
		fireErrorAnalytics: ((input: AnalyticsPayload) => unknown) & AbstractServiceMarker;
		getCurrentCloudId: (() => string) & AbstractServiceMarker;
		getCurrentProduct: (() => Product) & AbstractServiceMarker;
	};
} = Object.assign(fetch3PInviteRecommendationsImpl, {
	dependencies,
}) satisfies AnyServiceWithDeps<Deps>;

export async function fetch3PInviteRecommendationsImpl(
	input: Recommendations3PInput,
	context: DependenciesContext<Deps>,
): Promise<UserInfo[]> {
	const deps = context.dependencies();
	const cloudId = input.targetCloudId ?? deps.getCurrentCloudId();
	const limit = input.limit;
	const product = input.targetProduct ?? deps.getCurrentProduct();
	try {
		const response = await deps.fetchJSON<Recommendation3PResult>({
			url: `/gateway/api/pi-gateway/frontend/api/1/recommend/invite-users?limit=${limit}&targetCloudId=${cloudId}&targetProduct=${product}`,
			method: 'GET',
		});
		return response.recommendations;
	} catch (error: any) {
		deps.fireErrorAnalytics({
			meta: {
				id: 'fetchInviteRecommendations3P',
				packageName: 'expansion-services',
				teamName: 'Groot',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		return [];
	}
}

export type MapToFeaturedUserInput = {
	tooltipRenderer: (user: UserInfo) => string;
	userInfo: UserInfo[];
};

const mapToFeaturedUserImpl = (
	input: MapToFeaturedUserInput,
): {
	avatarUrl: string;
	email: string;
	id: string;
	name: string;
	source: string;
	tooltip: string;
}[] => {
	return input.userInfo.map((user, index) => ({
		email: user.email,
		name: user.displayName,
		avatarUrl: user.avatarUrl,
		source: user.source,
		tooltip: input.tooltipRenderer(user),
		id: user.atlassianAccountId ?? user.thirdPartyUserId,
	}));
};

export const mapToFeaturedUser: ((input: MapToFeaturedUserInput) => {
	avatarUrl: string;
	email: string;
	id: string;
	name: string;
	source: string;
	tooltip: string;
}[]) & {
	dependencies: {};
} = Object.assign(mapToFeaturedUserImpl, {
	dependencies: {},
}) satisfies AnyServiceWithDeps<{}>;
